
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Copy, Send, Info, ToggleLeft, ToggleRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export const SmsTab = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [useOtpEndpoint, setUseOtpEndpoint] = useState(false);
  const [formData, setFormData] = useState({
    partnerID: "13693",
    message: "Greetings from LamuLamu",
    shortcode: "TextSMS",
    mobile: "254700230965"
  });
  const [response, setResponse] = useState<any>(null);

  // Generate callback URL based on current domain
  const callbackUrl = `${window.location.origin}/api/sms-callback`;

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied!",
        description: "Callback URL copied to clipboard",
      });
    } catch (err) {
      toast({
        title: "Failed to copy",
        description: "Please copy the URL manually",
        variant: "destructive",
      });
    }
  };

  const sendSms = async () => {
    setIsLoading(true);
    setResponse(null);

    try {
      // Prepare the request data based on endpoint type
      const requestData = useOtpEndpoint 
        ? {
            ...formData,
            endpoint: 'otp'
          }
        : formData;

      const { data, error } = await supabase.functions.invoke('send-sms', {
        body: requestData
      });

      if (error) {
        throw error;
      }

      setResponse(data);
      toast({
        title: "SMS Sent",
        description: `SMS has been sent successfully via ${useOtpEndpoint ? 'OTP' : 'regular'} endpoint`,
      });
    } catch (error: any) {
      console.error('SMS Error:', error);
      toast({
        title: "SMS Failed",
        description: error.message || "Failed to send SMS",
        variant: "destructive",
      });
      setResponse({ error: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const currentEndpoint = useOtpEndpoint 
    ? "https://sms.textsms.co.ke/api/services/sendotp/"
    : "https://sms.textsms.co.ke/api/services/sendsms/";

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            SMS Testing
          </CardTitle>
          <CardDescription>
            Test SMS functionality using the TextSMS.co.ke API with endpoint switching
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Endpoint Toggle */}
          <div className="flex items-center justify-between p-4 border rounded-md bg-gray-50">
            <div className="space-y-1">
              <Label className="text-sm font-medium">API Endpoint</Label>
              <p className="text-sm text-muted-foreground">
                {useOtpEndpoint ? "OTP Endpoint (sendotp)" : "Regular SMS Endpoint (sendsms)"}
              </p>
              <p className="text-xs text-muted-foreground font-mono">
                {currentEndpoint}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setUseOtpEndpoint(!useOtpEndpoint)}
              className="flex items-center gap-2"
            >
              {useOtpEndpoint ? (
                <>
                  <ToggleRight className="h-4 w-4 text-primary" />
                  OTP
                </>
              ) : (
                <>
                  <ToggleLeft className="h-4 w-4" />
                  Regular
                </>
              )}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="partnerID">Partner ID</Label>
              <Input
                id="partnerID"
                value={formData.partnerID}
                onChange={(e) => handleInputChange("partnerID", e.target.value)}
                placeholder="13693"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="shortcode">Shortcode</Label>
              <Input
                id="shortcode"
                value={formData.shortcode}
                onChange={(e) => handleInputChange("shortcode", e.target.value)}
                placeholder="TextSMS"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mobile">Mobile Number</Label>
              <Input
                id="mobile"
                value={formData.mobile}
                onChange={(e) => handleInputChange("mobile", e.target.value)}
                placeholder="254700230965"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleInputChange("message", e.target.value)}
              placeholder="Greetings from LamuLamu"
              rows={3}
            />
          </div>

          <Button 
            onClick={sendSms} 
            disabled={isLoading}
            className="w-full"
          >
            {isLoading ? "Sending..." : `Send SMS via ${useOtpEndpoint ? 'OTP' : 'Regular'} Endpoint`}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            Callback URL
          </CardTitle>
          <CardDescription>
            Use this URL for SMS delivery status callbacks
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <Input
              value={callbackUrl}
              readOnly
              className="font-mono text-sm"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={() => copyToClipboard(callbackUrl)}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {response && (
        <Card>
          <CardHeader>
            <CardTitle>Response</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="bg-gray-100 p-4 rounded-md overflow-x-auto text-sm">
              {JSON.stringify(response, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <strong>Current Endpoint:</strong> {currentEndpoint}
          <br />
          <strong>Method:</strong> POST
          <br />
          <strong>SMS_KEY:</strong> Retrieved from Supabase secrets
        </AlertDescription>
      </Alert>
    </div>
  );
};
